import React, { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Filament } from './Filament';
import { OrbitSlot } from './OrbitSlot';
import { StageCardView } from '../StageCardView';
import { usePointerParallax } from '../../../hooks/usePointerParallax';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import { cardAgents } from '../../../data/cardAgents';
import { getAgent } from '../../../utils/agents';
import { layoutOrbit, type WallGeometry } from '../../../utils/orbitLayout';
import type { Box } from '../../../utils/agendaGeometry';
import type { AgentId, SoundId } from '../../../types/anna';
import type { StageCard } from '../../../types/stage';
interface OrbitCardsProps {
  g: WallGeometry;
  cards: StageCard[];
  onRemove: (uid: string) => void;
  onApproved: (agent: AgentId) => void;
  onSound: (id: SoundId) => void;
  /** Areas cards must keep clear of (agenda labels), relative to Anna's core */
  obstacles?: Box[];
}
const DEFAULT_H = 260;
const NO_OBSTACLES: Box[] = [];

/** Summoned cards in orbit around Anna, each tied to her core by a filament. The agenda is drawn as an arc instead. */
export function OrbitCards({
  g,
  cards,
  onRemove,
  onApproved,
  onSound,
  obstacles = NO_OBSTACLES
}: OrbitCardsProps) {
  const reduce = useMotionPreference();
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [pings, setPings] = useState<Record<string, number>>({});
  const orbiting = useMemo(() => cards.filter(c => c.kind !== 'vandaag'), [cards]);
  const layout = useMemo(() => layoutOrbit(orbiting, heights, g, obstacles), [orbiting, heights, g, obstacles]);
  const parallax = usePointerParallax(reduce ? 0 : 7);
  const measure = useCallback((uid: string, h: number) => setHeights(prev => prev[uid] === h ? prev : {
    ...prev,
    [uid]: h
  }), []);
  const ping = useCallback((uid: string) => setPings(prev => ({
    ...prev,
    [uid]: (prev[uid] ?? 0) + 1
  })), []);
  return <motion.div className="pointer-events-none absolute inset-0 z-10" style={{
    x: parallax.x,
    y: parallax.y
  }} data-id="element-1652" data-mp="21t1slknjgaqy">
      <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden data-id="element-1653" data-mp="25tbyyyf8t36c">
        <AnimatePresence data-id="element-1654" data-mp="2akbtjx0w395o">
          {orbiting.map(c => {
          const p = layout[c.uid];
          return <Filament key={c.uid} id={c.uid} x1={g.cx} y1={g.cy} x2={g.cx + p.anchorX} y2={g.cy + p.y} color={getAgent(cardAgents[c.kind]).color} ping={pings[c.uid] ?? 0} data-id="element-1655" data-mp="2fivfvyjeamyk" />;
        })}
        </AnimatePresence>
      </svg>
      <div className="absolute inset-0" style={{
      perspective: 1800
    }} data-id="element-1656" data-mp="1wtymrxzqi0hx">
        <AnimatePresence data-id="element-1657" data-mp="1wr2pmbvpnfuj">
          {orbiting.map(c => <OrbitSlot key={c.uid} uid={c.uid} p={layout[c.uid]} g={g} h={heights[c.uid] ?? DEFAULT_H} reduce={reduce} onMeasure={measure} onPing={ping} data-id="element-1658" data-mp="338zhy1gxqeng">
              <StageCardView card={c} onRemove={onRemove} onApproved={onApproved} onSound={onSound} data-id="element-1659" data-mp="wkz3k7wmpm2k" />
            </OrbitSlot>)}
        </AnimatePresence>
      </div>
    </motion.div>;
}